import { PDFDocument, PDFName } from 'pdf-lib';
import pdfMake from 'pdfmake/build/pdfmake';
import FacturXService from './FacturXService';
import LogoService from './LogoService';
import DataService from './DataService';
import LogoFXm from './LogoFXm';
//import SVGService from './SVGService';

// Importation correcte des polices
import pdfFonts from 'pdfmake/build/vfs_fonts';
// Initialisation des polices pour pdfMake
pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;

class PDFDocumentService {
    constructor() {
        this.logoService = new LogoService();
        //this.svgService = new SVGService();

        // Définition des styles utilisés dans le document
        this.styles = {
            header: {
                fontSize: 16,
                color: '#1E3F66',
                margin: [0, 20, 0, 0],
                alignment: 'right'
            },
            title: {
                fontSize: 14,
                bold: true,
                margin: [0, 20, 0, 10],
                color: '#1E3F66'
            },
            tableHeader: {
                bold: true,
                fontSize: 10,
                color: '#1E3F66'
            },
            footer: {
                fontSize: 8,
                color: '#666666',
                alignment: 'center'
            },
            sectionTitle: {
                bold: true,
                color: '#1E3F66',
                fontSize: 12,
                alignment: 'center'
            },
            normal: {
                fontSize: 10
            },
            mainRow: {
                fontSize: 10,
                bold: true,
                margin: [0, 5, 0, 0]
            },
            subRow: {
                fontSize: 8,
                italics: true,
                margin: [13, 2, 0, 0]
            },
            totalRow: {
                fontSize: 10,
                bold: true,
                margin: [0, 2, 0, 2]
            },
            customerHeader: {
                fontSize: 10,
                alignment: 'right'
            }
        };
    }

    /**
     * Génère dynamiquement le tableau des prestations avec les lignes principales et les sous-lignes.
     * Les lignes principales affichent un prix et les sous-lignes, indentées, n'affichent pas de prix.
     * @param {Object} data - Les données du devis qui contiennent la liste des prestations dans data.items.
     */
    generatePrestationsTable(data) {
        const body = [];
        const showSubPrices = data.showSubPrices;

        // Ligne d'en-tête du tableau
        const headerRow = [{ text: 'DÉSIGNATION', style: 'tableHeader' }];
        if (showSubPrices) {
            headerRow.push(
                { text: 'QTÉ', style: 'tableHeader', alignment: 'right' },
                { text: 'P.U.', style: 'tableHeader', alignment: 'right' }
            );
        }
        headerRow.push({ text: 'MONTANT', style: 'tableHeader', alignment: 'right' });
        body.push(headerRow);

        data.items.forEach(item => {
            // Ligne principale avec le prix
            const mainRow = [{ text: item.name, style: 'mainRow' }];
            if (showSubPrices) {
                mainRow.push(
                    { text: '', style: 'mainRow' },
                    { text: '', style: 'mainRow' }
                );
            }
            mainRow.push({
                text: (item.price !== undefined && item.price !== 0)
                    ? item.price.toFixed(2) + ' €'
                    : '',
                style: 'mainRow',
                alignment: 'right',
                marginRight: 5
            });
            body.push(mainRow);

            // Ajout des sous-lignes indentées (sans prix)
            if (item.subItems && item.subItems.length > 0) {
                item.subItems.forEach((subItem) => {
                    const subRow = [{ text: subItem.name, style: 'subRow' }];
                    
                    if (showSubPrices) {
                        const subItemAmount = (subItem.quantity && subItem.unitPrice) ? subItem.quantity * subItem.unitPrice : 0;
                        subRow.push(
                            { text: subItem.quantity ? subItem.quantity.toString() : '', style: 'subRow', alignment: 'right' },
                            { text: subItem.unitPrice ? subItem.unitPrice.toFixed(2) + ' €' : '', style: 'subRow', alignment: 'right' },
                            { text: subItemAmount !== 0 ? subItemAmount.toFixed(2) + ' €' : '', style: 'subRow', alignment: 'right' }
                        );
                    } else {
                         subRow.push({ text: '', style: 'subRow', alignment: 'right' });
                    }
                    
                    body.push(subRow);
                });
            }
        });

        const widths = ['*'];
        if (showSubPrices) {
            widths.push('auto', 'auto');
        }
        widths.push('auto');

        return {
            table: {
                headerRows: 1,
                widths: widths,
                body: body
            },
            layout: 'noBorders'
        };
    }

    /**
   * Génère dynamiquement le tableau totaux
   * @param {Object} data - Les données du devis qui contiennent la liste des prestations dans data.items.
   */
    generateTotalTable(data) {
        const body = [];

        // Ajouter les totaux sous le tableau
        body.push(
            [{ rowSpan: 3, text: 'Le coût des prestations est déterminé en décomposant chaque élément de mission en heure d’activité et en frais divers.', fontSize: 8, italics: true, margin: [0, 18, 10, 0] },
            { text: 'TOTAL HT (hors option)', style: 'totalRow', alignment: 'right', fillColor: '#3A5F8B', color: 'white' },
            { text: data.total.toFixed(2) + ' €', style: 'totalRow', alignment: 'right', fillColor: '#3A5F8B', color: 'white' }
            ],
            ['',
                { text: 'TVA', style: 'totalRow', alignment: 'right' },
                { text: data.vat.toFixed(2) + ' €', style: 'totalRow', alignment: 'right' }
            ],
            ['',
                { text: 'TOTAL TTC', style: 'totalRow', alignment: 'right', fillColor: '#3A5F8B', color: 'white' },
                { text: data.totalWithVat.toFixed(2) + ' €', style: 'totalRow', alignment: 'right', fillColor: '#3A5F8B', color: 'white' }
            ]
        );

        return {
            table: {
                headerRows: 0,
                widths: ['*', 110, 65],
                body: body
            },
            layout: { hLineWidth: () => 1, vLineWidth: () => 1, hLineColor: 'white', vLineColor: 'white' }
        };
    }

    generateQuoteId(data) {
        return [
            {
                canvas: [
                    {
                        type: 'rect',
                        x: 0,
                        y: 0,
                        w: 232,
                        h: 40,
                        r: 5,
                        lineColor: '#3A5F8B',
                    }
                ]
            },
            {
                text: `${data.docType} N° ${data.docNumber}`,
                style: 'header',
                bold: true,
                alignment: 'center',
                relativePosition: { y: -30 },
            }
        ];
    }

    async generateSignBox(signText) {

            return {
                stack: [
                    {
                        canvas: [
                            {
                                type: 'rect',
                                x: 0,
                                y: 0,
                                w: 232,
                                h: 70,
                                r: 5,
                                lineColor: '#3A5F8B',
                            }
                        ]
                    },
                    {
                        text: signText,
                        style: 'normal',
                        fontSize: 8,
                        color: '#3A5F8B',
                        alignment: 'left',
                        margin: [5, -65, 0, 0],  // Remplace relativePosition
                    }
                ],
                keepTogether: true  // Garantit que les éléments restent ensemble sur la même page
            };
    }

    generateBillInfoBox(Billtext, yPos = -63) {
        return {
            stack:  [
            {
                canvas: [
                    {
                        type: 'rect',
                        x: 0,
                        y: 0,
                        w: 232,
                        h: 70,
                        r: 5,
                        lineColor: '#3A5F8B',
                    }
                ]
            },
            {
                text: Billtext,
                style: 'normal',
                fontSize: 7,
                color: '#3A5F8B',
                alignment: 'center',
                margin: [0, yPos, 0, 0],  // Remplace relativePosition
            }
        ],
        keepTogether: true
    };
    }

    generateWriteTo(data) {
        return [
            {
                canvas: [
                    {
                        type: 'rect',
                        x: 0,
                        y: 0,
                        w: 232,
                        h: 50,
                        r: 5,
                        lineColor: '#3A5F8B',
                    }
                ]
            },
            {
                stack: [
                    { text: `${data.clientName}`, bold: true, margin: [0, 0, 0, 5] },
                    { text: `${data.clientAddress.line1}`, margin: [0, 0, 0, 2] },
                    { text: `${data.clientAddress.line2}` },
                ],
                alignment: 'center',
                relativePosition: { y: -45 },
            }
        ];
    }

    generateSubHeader(data) {
        const body = [
            [
                { text: 'En date du', style: 'normal', border: [false, false, false, false] },
                { text: `${data.date}`, style: 'normal', bold: true, border: [false, false, false, false], alignment: 'right' }
            ],
            [
                { text: 'Référence client :', style: 'normal', border: [false, false, false, false] },
                { text: `${data.refClient}`, style: 'normal', bold: true, border: [false, false, false, false], alignment: 'right' }
            ],
            [
                { text: 'A l\'attention de :', style: 'normal', border: [false, false, false, false] },
                { text: `${data.attention}`, style: 'normal', bold: true, border: [false, false, false, false], alignment: 'right' }
            ]
        ];

        // Ajout conditionnel de la référence de commande
        if (data.command) {
            body.push([
                { text: 'Référence commande :', style: 'normal', border: [false, false, false, false] },
                { text: `${data.command}`, style: 'normal', bold: true, border: [false, false, false, false], alignment: 'right' }
            ]);
        }

        return [{
            table: {
                widths: ['40%', '*'],
                body: body
            },
            layout: {
                defaultBorder: false,
                paddingLeft: () => 5,
                paddingRight: () => 5,
                paddingTop: () => 0,
                paddingBottom: () => 0
            },
            margin: [0, 10, 0, 10]
        }];
    }

    generateContactTo() {
        return [
            {
                columns: [
                    {
                        width: '40%', stack: [{ text: `Téléphone :`, style: 'normal', alignment: 'left', margin: [0, 10, 0, 5] },
                        { text: `Mail :`, style: 'normal', alignment: 'left', margin: [0, 0, 0, 5] },
                        ]
                    },
                    {
                        width: '60%', stack: [{ text: `01 02 03 04 05`, style: 'normal', alignment: 'right', bold: true, margin: [0, 10, 20, 5] },
                        { text: `contact@acmedemo.com`, style: 'normal', alignment: 'right', bold: true, margin: [0, 0, 20, 5] },
                        ]
                    }
                ],
                margin: [0, 0, 0, 20]
            }];
    }

    generateLine() {
        return {
            canvas: [
                {
                    type: 'line',
                    x1: 0,     // Coordonnée de départ en x
                    y1: 0,      // Coordonnée de départ en y
                    x2: 515,    // Coordonnée d'arrivée en x
                    y2: 0,      // Coordonnée d'arrivée en y
                    lineWidth: 1,    // Épaisseur de la ligne
                    lineColor: '#3A5F8B', // Couleur de la ligne
                    margin: [0, 0, 0, 5]
                }
            ]
        };
    }

    /**
     * Génère le reste de la première page du devis.
     * @param {Object} data - Les données du devis.
     * @param {Object} customer - Les informations du client.
     */
    async generateQuoteFirstPage(data) {
        return [
            // En-tête avec logo et références devis
            this.generateHeader(data),

            // Présentation du projet
            this.generateProjectTitle(data),

            // Tableau des prestations
            this.generatePrestationsTable(data),

            // Tableau des totaux
            this.generateTotalTable(data),

            // Information additionnelles
            await this.generateQuoteAdditionalIformations(data),
        ];
    }

    generateProjectTitle(data) {
        return [

            this.generateLine(),

            // Présentation du projet
            { text: `PROJET : ${data.project}`, style: 'sectionTitle', margin: [0, 5, 0, 5] },

            this.generateLine(),

            { text: '', margin: [0, 0, 0, 15] },
        ];
    }


    generateHeader(data) {
        return [
            {
                columns: [
                    { width: '45%', stack: [this.logoService.generateLogo()] },
                    { width: '10%', text: '' },
                    { width: '45%', stack: [this.generateQuoteId(data), this.generateSubHeader(data)] }
                ],
                margin: [0, 0, 0, 10]
            },

            {
                columns: [
                    { width: '45%', stack: [this.generateContactTo()] },
                    { width: '10%', text: '' },
                    { width: '45%', stack: [this.generateWriteTo(data)] }
                ],
                margin: [0, 0, 0, 10]
            },
        ];
    }

    /**
     * Compte le nombre de lignes (items) et de sous-lignes (subItems) dans les données
     * @param {Object} data - Les données du document
     * @returns {Object} Un objet contenant le nombre de lignes et de sous-lignes
     */
    countLinesAndSublines(data) {
        let lines = 0;
        let sublines = 0;
        
        if (data.items && Array.isArray(data.items)) {
            lines = data.items.length;
            
            data.items.forEach(item => {
                if (item.subItems && Array.isArray(item.subItems)) {
                    sublines += item.subItems.length;
                }
            });
        }
        
        return { lines, sublines };
    }

    /**
     * Calcule une marge dynamique inversement proportionnelle au nombre de lignes
     * @param {Number} lines - Le nombre de lignes
     * @param {Number} sublines - Le nombre de sous-lignes
     * @param {Number} minMargin - La marge minimum (en points)
     * @param {Number} maxMargin - La marge maximum (en points)
     * @returns {Number} La marge calculée
     */
    calculateDynamicMargin(lines, sublines, minMargin, maxMargin) {
        // Poids relatif des lignes et sous-lignes (une ligne compte plus qu'une sous-ligne)
        const lineHeight = 15; // Hauteur approximative d'une ligne principale en points
        const sublineHeight = 10; // Hauteur approximative d'une sous-ligne en points
        
        // Calcul de l'espace utilisé par les lignes et sous-lignes
        const totalHeight = (lines * lineHeight) + (sublines * sublineHeight);
        // Facteur d'échelle - à ajuster selon vos besoins
        // Représente la hauteur totale à partir de laquelle la marge sera minimale
        const heightThreshold = 200; 
        
        // Calcul de la marge - inversement proportionnelle au nombre de lignes
        let margin = maxMargin - ((totalHeight / heightThreshold) * (maxMargin - minMargin));
        
        // S'assurer que la marge reste dans les limites spécifiées
        margin = Math.max(minMargin, Math.min(maxMargin, margin));
        
        // Arrondir pour plus de propreté
        return Math.round(margin);
    }

    async generateQuoteAdditionalIformations(data) {
        // Calcul de la marge dynamique pour les boîtes de signature
        const { lines, sublines } = this.countLinesAndSublines(data);
        const dynamicMargin = this.calculateDynamicMargin(lines, sublines, 10, 60);

        return [
            {
                columns: [
                    { width: '45%', stack: [this.generateAdditionalIformations('Notes', data.notes)] },
                    { width: '10%', text: '' },
                    { width: '45%', stack: [this.generateAdditionalIformations('Delais', data.delay)] }
                ],
                margin: [0, 15, 0, 0]
            },
            {
                columns: [
                    { width: '45%', stack: [this.generateAdditionalIformations('A la charge du client', data.needsFromClient)] },
                    { width: '10%', text: '' },
                    { width: '45%', stack: [this.generateAdditionalIformations('Hors mission', data.outOfScope)] }
                ],
                margin: [0, 5, 0, 0]
            },

            {
                columns: [
                    { width: '45%', stack: [await this.generateSignBox('Pour ACME, date et signature')] },
                    { width: '10%', text: '' },
                    { width: '45%', stack: [await this.generateSignBox('Pour le client, date et Signature')] }
                ],
                margin: [0, dynamicMargin, 0, 0]  // Utilisation de la marge dynamique ici
            },
        ];
    }

    /**
   * Génère les infos supplémentaires
   */
    generateAdditionalIformations(title, informations) {
        if (informations) {
            return [
                { text: title, bold: true, margin: [0, 10, 0, 0] },
                { text: informations, style: 'normal', margin: [0, 0, 0, 0], fontSize: 8, italics: true }
            ];
        }
    }

    /**
     * Génère la page des conditions générales de vente.
     */
    generateGeneralConditionsPage() {
        return [
            { text: 'CONDITIONS GÉNÉRALES DE VENTE', style: 'sectionTitle', margin: [0, 10, 0, 10] },
            {
                text:
                    'Les présentes conditions générales de vente ont pour objet de régir les relations contractuelles entre la société ACME et le client.',
                style: 'normal',
                margin: [0, 5, 0, 15]
            },
            { text: 'Article 1-ENGAGEMENT CONTRACTUEL', style: 'sectionTitle', margin: [0, 10, 0, 5] },
            {
                text:
                    'ACME sera engagé à réaliser l\'ensemble des prestations indiquées sur le devis moyennant rémunération par le client des honoraires indiqués sur ce même devis.',
                style: 'normal',
                margin: [0, 5, 0, 15]
            },
            { text: 'Article 2 - DURÉE DE L\'OFFRE ET PRIX', style: 'sectionTitle', margin: [0, 10, 0, 5] },
            {
                text:
                    'Les offres de services proposées sont valables pendant 1 jour après la date de réalisation du devis.',
                style: 'normal',
                margin: [0, 5, 0, 15]
            },
            { text: 'Article 3 - FACTURATION - RÈGLEMENTS', style: 'sectionTitle', margin: [0, 10, 0, 5] },
            {
                text:
                    'Les factures seront transmises suivant l\'avancement des prestations. Sauf cas contraire, il ne sera pas demandé d\'acompte à la commande. Les règlements s\'effectuent par virement bancaire ou par chèque à l\'ordre de ACME. Les règlements sont à effectuer au maximum à 30 jours fin de mois.',
                style: 'normal',
                margin: [0, 5, 0, 15]
            },
            { text: 'Article 4 - MODIFICATIONS DU CONTRAT', style: 'sectionTitle', margin: [0, 10, 0, 5] },
            {
                text:
                    'Selon les besoins du client, ACME pourra être mandatée pour des missions complémentaires qui feront l\'objet d\'avenants au contrat, signés par les parties.',
                style: 'normal',
                margin: [0, 5, 0, 15]
            }
        ];
    }

    // Nouvelles méthodes pour les factures
    generateBillContent(data) {
        return [
            // En-tête avec logo et références devis
            this.generateHeader(data),

            // Présentation du projet
            this.generateProjectTitle(data),

            // Tableau de facturation
            this.generateBillTable(data),

            // Tableau des totaux de la facture
            this.generateBillTotalTable(data),

            // Conditions de paiement
            this.generatePaymentConditions(data),  // Passage du paramètre data pour le calcul de marge
        ];
    }

    // Méthode de génération du tableau pour les factures
    generateBillTable(data) {
        const body = [];

        // En-tête du tableau
        body.push([
            { text: 'DÉSIGNATION', style: 'tableHeader' },
            { text: 'PRIX', style: 'tableHeader', alignment: 'right' },
            { text: 'AVANCEMENT', style: 'tableHeader', alignment: 'right' },
            { text: 'MONTANT', style: 'tableHeader', alignment: 'right' }
        ]);

        // Lignes du tableau
        data.items.forEach(item => {
            body.push([
                { text: item.name, style: 'mainRow' },
                { text: (item.price !== undefined && item.price !== 0 ) ? item.price.toFixed(2) + ' €' : '', style: 'mainRow', alignment: 'right' },
                { text: (item.progress !== undefined && item.price !== 0 ) ? item.progress.toFixed(0) + ' %' : '', style: 'mainRow', alignment: 'right' },
                { text: (item.amount !== undefined && item.price !== 0 ) ? item.amount.toFixed(2) + ' €' : '', style: 'mainRow', alignment: 'right' }
            ]);

            // Sous-lignes
            if (item.subItems && item.subItems.length > 0) {
                item.subItems.forEach(subItem => {
                    body.push([
                        { text: subItem.name, style: 'subRow' },
                        { text: '', style: 'subRow', alignment: 'right' },
                        { text: '', style: 'subRow', alignment: 'right' },
                        { text: '', style: 'subRow', alignment: 'right' }
                    ]);
                });
            }
        });

        return {
            table: {
                headerRows: 1,
                widths: ['*', 'auto', 'auto', 'auto'],
                body: body
            },
            layout: 'noBorders'
        };
    }

    // Méthode pour générer le tableau des totaux de la facture
    generateBillTotalTable(data) {
        const body = [];
        const generalConditions = this.generateBillGeneralConditions(data);

        body.push(
            [
                '',
                { text: 'DEVIS HT', style: 'totalRow', alignment: 'right', fillColor: '#3A5F8B', color: 'white' },
                { text: data.total.toFixed(2) + ' €', style: 'totalRow', alignment: 'right', fillColor: '#3A5F8B', color: 'white' }
            ],
            [
                { rowSpan: 4, stack: generalConditions },
                { text: 'DÉJÀ FACTURÉ', style: 'totalRow', alignment: 'right' },
                { text: data.alreadyPaid.toFixed(2) + ' €', style: 'totalRow', alignment: 'right' }
            ],
            [
                '',
                { text: 'FACTURE HT', style: 'totalRow', alignment: 'right' },
                { text: data.currentBillAmount.toFixed(2) + ' €', style: 'totalRow', alignment: 'right' }
            ],
            [
                '',
                { text: 'TVA FACTURE', style: 'totalRow', alignment: 'right' },
                { text: (data.vat).toFixed(2) + ' €', style: 'totalRow', alignment: 'right' }
            ],
            [
                '',
                { text: 'FACTURE TTC', style: 'totalRow', alignment: 'right', fillColor: '#3A5F8B', color: 'white' },
                { text: (data.totalWithVat).toFixed(2) + ' €', style: 'totalRow', alignment: 'right', fillColor: '#3A5F8B', color: 'white' }
            ]
        );

        return {
            table: {
                headerRows: 0,
                widths: ['*', 110, 65],
                body: body
            },
            layout: { hLineWidth: () => 1, vLineWidth: () => 1, hLineColor: 'white', vLineColor: 'white' }
        };
    }

    generateBillGeneralConditions(data) {
        return [
            {
                columns: [
                    {
                        width: '50%', stack: [
                            { text: `Facture relative au devis n°`, fontSize: 8, alignment: 'left', bold: true, margin: [30, 4, 0, 5] },
                            { text: `Date d'échéance`, fontSize: 8, alignment: 'left', bold: true, margin: [30, 0, 0, 5] },
                            { text: `Mode de règlement`, fontSize: 8, alignment: 'left', bold: true, margin: [30, 0, 0, 5] },
                            { text: `N°TVA intracommunautaire `, fontSize: 8, alignment: 'left', bold: true, margin: [30, 0, 0, 5] },
                        ]
                    },
                    {
                        width: '50%', stack: [
                            { text: `${data.quoteNumber}`, fontSize: 8, alignment: 'right', margin: [0, 4, 40, 5] },
                            { text: `${data.dueDate || 'Non définie'}`, fontSize: 8, alignment: 'right', margin: [0, 0, 40, 5] },
                            { text: `chèque ou virement`, fontSize: 8, alignment: 'right', margin: [0, 0, 40, 5] },
                            { text: `FR 12832054845`, fontSize: 8, alignment: 'right', margin: [0, 0, 40, 5] },
                        ]
                    }
                ],
                margin: [0, 0, 0, 20]
            }
        ];
    }

    // Méthode pour générer les conditions de paiement
    generatePaymentConditions(data) {
        // Calcul de la marge dynamique pour les boîtes d'information
        const { lines, sublines } = this.countLinesAndSublines(data);
        const dynamicMargin = this.calculateDynamicMargin(lines, sublines, 10, 60);

        return [
            {
                columns: [
                    { width: '45%', stack: [this.generateBillInfoBox('DESERT BANK - CANYON CITY \n' +
                                                                    'Code Banque 42424 - Code Guichet 31415\n' + 
                                                                    'Compte 27182818284 Clé 59\n' + 
                                                                    'IBAN : FR7642424314152718281828459 - BIC : BIPBIP', -50)] },
                    { width: '10%', text: '' },
                    { width: '45%', stack: [this.generateBillInfoBox('Conformément à l\'article L.441-6 du code de commerce, des pénalités de retard sont dues à défaut de règlement le jour suivant\n' +
                                                                    'la date de paiement figurant sur la facture. Le taux d\'intérêt de ces pénalités de retard est de 2,13% annuels.\n' + 
                                                                    'Conformément à l\'article L.441-3 du code de commerce, une indémnité forfaitaire de 40€ pour frais de recouvrement sera\n'+
                                                                    'appliquée en cas de retard de paiement.')] }
                ],
                margin: [0, dynamicMargin, 0, 0]  // Utilisation de la marge dynamique ici au lieu de 30
            },
        ];
    }

    /**
     * Génère l'intégralité du PDF du devis en combinant la première page et la page des conditions générales.
     * @param {Object} data - Les données du devis.
     * @param {String} documentType - Type de document ('quote' ou 'INVOICE')
     */
    async generatePDF(data, documentType = 'quote') {
        try {
            const content = documentType === 'quote'
                ? await this.generateQuoteContent(data)
                : await this.generateBillContent(data);
                
            const docDefinition = {
                version: '1.7',
                subset: 'PDF/A-3a',
                tagged: true,                
                pageSize: 'A4',
                pageMargins: [40, 60, 40, 60],
                content: content,
                styles: this.styles,
                defaultStyle: {
                    fontSize: 10
                },
                footer: this.generateFooter(documentType === 'INVOICE')
            };
            
            // Générer le PDF standard
            const pdfBuffer = await this.generatePDFBuffer(docDefinition);
            
            // Générer le nom de fichier au format AAAAMMJJ_clientName_numéro
            const fileName = this.generateFileName(data);
            
            if (documentType === 'INVOICE') {
                // Pour les factures, ajouter le XML Factur-X avec les métadonnées XMP
                const bill = await this.getBillFromData(data);
                const facturXService = new FacturXService();
                const xmlData = await facturXService.generateXML(bill, data);
                const finalPdf = await this.attachXMLToPDF(pdfBuffer, xmlData, data, documentType, fileName);
                
                // Sauvegarder la facture avec Factur-X et métadonnées
                this.savePDF(finalPdf, fileName);
            } else {
                // Pour les devis, utiliser la même méthode attachXMLToPDF mais sans XML
                // Cela permet d'avoir les mêmes métadonnées au format XMP que pour les factures
                const finalPdf = await this.attachXMLToPDF(pdfBuffer, "", data, documentType, fileName);
                
                // Sauvegarder directement
                this.savePDF(finalPdf, fileName);
            }
        } catch (error) {
            console.error("Erreur lors de la génération du PDF:", error);
        }
    }

    /**
     * Génère un buffer PDF à partir d'une définition de document
     * @param {Object} docDefinition - Définition du document pour pdfMake
     * @returns {Promise<Uint8Array>} Buffer contenant le PDF
     */
    async generatePDFBuffer(docDefinition) {
        return new Promise((resolve) => {
            const pdfDocGenerator = pdfMake.createPdf(docDefinition);
            pdfDocGenerator.getBuffer((buffer) => {
                resolve(buffer);
            });
        });
    }

    /**
     * Génère le nom de fichier au format AAAAMMJJ_clientName_numéro
     * @param {Object} data - Les données du document
     * @returns {String} Nom du fichier formaté
     */
    generateFileName(data) {
        // Utiliser la date du document au lieu de la date courante
        const dateParts = data.date.split('/');
        if (dateParts.length === 3) {
            const day = dateParts[0];
            const month = dateParts[1];
            const year = dateParts[2];
            
            // Extraire un nom de client propre (sans caractères spéciaux et espaces remplacés par des tirets)
            const clientName = data.clientName.replace(/[^\w\s]/gi, '').replace(/\s+/g, '-');
            
            return `${year}${month}${day}_${clientName}_${data.docNumber}.pdf`;
        } else {
            // Si le format de date est incorrect, utiliser un format par défaut
            console.warn("Format de date incorrect, utilisation du format par défaut");
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            
            const clientName = data.clientName.replace(/[^\w\s]/gi, '').replace(/\s+/g, '-');
            
            return `${year}${month}${day}_${clientName}_${data.docNumber}.pdf`;
        }
    }

    /**
     * Sauvegarde un buffer PDF sous forme de fichier téléchargeable
     * @param {Uint8Array} pdfBuffer - Buffer contenant le PDF
     * @param {String} filename - Nom du fichier à sauvegarder
     */
    savePDF(pdfBuffer, filename) {
        // Créer un Blob et proposer le téléchargement
        const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
    }

    async getBillFromData(data) {
        // Récupérer l'objet Bill à partir des données du document
        return await DataService.getBillByCustomId(data.docNumber);
    }

    async attachXMLToPDF(pdfBuffer, xmlData, data, documentType, fileName) {
        // Charger le PDF
        const pdfDoc = await PDFDocument.load(pdfBuffer);
        
        // N'attacher le XML que s'il est fourni et non vide (cas des factures)
        if (xmlData && xmlData.trim() !== '') {
            // Incorporer le XML comme pièce jointe en utilisant TextEncoder au lieu de Buffer
            const encoder = new TextEncoder();
            const xmlBytes = encoder.encode(xmlData);
            
            // Attacher le fichier XML
            // eslint-disable-next-line
            await pdfDoc.attach(
                xmlBytes,
                'factur-x.xml',
                {
                    mimeType: 'text/xml',
                    description: 'Factur-X XML',
                    afRelationship: 'Data'
                }
            );
        }
        
        // Créer le bloc XMP avec les tags requis par Factur-X
        const xmpMetadata = this.generateXMPMetadata(data, documentType, fileName);
        
        // Injecter les métadonnées XMP dans le document via l'API standard de pdf-lib
        try {
            // Créer les données XMP en tant que tableau d'octets
            const xmpBytes = new TextEncoder().encode(xmpMetadata);
            
            // Créer un objet indirect pour le stream de métadonnées
            const metadataStreamRef = pdfDoc.context.nextRef();
            
            // Créer le dictionnaire du stream avec toutes les propriétés requises
            const metadataDict = pdfDoc.context.obj({
                Type: 'Metadata',
                Subtype: 'XML',
                Length: xmpBytes.length,
                Filter: undefined // Pas de compression pour éviter les problèmes
            });
            
            // Créer le stream de métadonnées sans compression
            const metadataStream = pdfDoc.context.stream(xmpBytes, metadataDict);
            
            // Assigner l'objet stream à la référence
            pdfDoc.context.assign(metadataStreamRef, metadataStream);
            
            // Accéder au catalogue du PDF (root dictionary) et assigner la référence
            const catalog = pdfDoc.context.lookup(pdfDoc.context.trailerInfo.Root);
            catalog.set(PDFName.of('Metadata'), metadataStreamRef);
            
        } catch (error) {
            console.error('Failed to inject XMP metadata:', error);
            // Continuer même en cas d'erreur pour ne pas bloquer la génération du PDF
        }
        
        // Sauvegarder le document
        return await pdfDoc.save();
    }
    
    generateXMPMetadata(data, documentType, fileName) {
        // Date actuelle au format ISO 8601
        const now = new Date().toISOString().replace(/\.\d+Z$/, 'Z');
        
        // Niveau de conformité basé sur le type de document
        const conformanceLevel = documentType === 'INVOICE' ? 'MINIMUM' : 'BASIC';
        
        // Version du schéma Factur-X
        const version = '1.0.7';
        
        // Créer le XML XMP avec les namespaces appropriés et la déclaration d'extension de schéma
        return `<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/">
  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
    <!-- Déclaration PDF/A Extension Schema -->
    <rdf:Description rdf:about=""
                     xmlns:pdfaExtension="http://www.aiim.org/pdfa/ns/extension/"
                     xmlns:pdfaSchema="http://www.aiim.org/pdfa/ns/schema#"
                     xmlns:pdfaProperty="http://www.aiim.org/pdfa/ns/property#">
      <pdfaExtension:schemas>
        <rdf:Bag>
          <rdf:li rdf:parseType="Resource">
            <pdfaSchema:schema>Factur-X PDFA Extension Schema</pdfaSchema:schema>
            <pdfaSchema:namespaceURI>urn:factur-x:pdfa:CrossIndustryDocument:invoice:1p0#</pdfaSchema:namespaceURI>
            <pdfaSchema:prefix>fx</pdfaSchema:prefix>
            <pdfaSchema:property>
              <rdf:Seq>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>DocumentType</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>${documentType}</pdfaProperty:description>
                </rdf:li>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>DocumentFileName</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>${fileName}</pdfaProperty:description>
                </rdf:li>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>Version</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>${version}</pdfaProperty:description>
                </rdf:li>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>ConformanceLevel</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>${conformanceLevel}</pdfaProperty:description>
                </rdf:li>
              </rdf:Seq>
            </pdfaSchema:property>
          </rdf:li>
        </rdf:Bag>
      </pdfaExtension:schemas>
    </rdf:Description>

    <!-- Métadonnées du document -->
    <rdf:Description rdf:about=""
                     xmlns:pdf="http://ns.adobe.com/pdf/1.3/"
                     xmlns:fx="urn:factur-x:pdfa:CrossIndustryDocument:invoice:1p0#"
                     xmlns:dc="http://purl.org/dc/elements/1.1/"
                     xmlns:xmp="http://ns.adobe.com/xap/1.0/"
                     xmlns:pdfaid="http://www.aiim.org/pdfa/ns/id/">
      <fx:DocumentType>${documentType}</fx:DocumentType>
      <fx:DocumentFileName>${fileName}</fx:DocumentFileName>
      <fx:Version>${version}</fx:Version>
      <fx:ConformanceLevel>${conformanceLevel}</fx:ConformanceLevel>
      
      <!-- Correction du format pour dc:title (lang alt) -->
      <dc:title>
        <rdf:Alt>
          <rdf:li xml:lang="x-default">${data.docNumber}</rdf:li>
        </rdf:Alt>
      </dc:title>
      
      <!-- Correction du format pour dc:creator (seq propername) -->
      <dc:creator>
        <rdf:Seq>
          <rdf:li>Q&amp;B</rdf:li>
        </rdf:Seq>
      </dc:creator>
      
      <!-- Correction du format pour dc:description (lang alt) -->
      <dc:description>
        <rdf:Alt>
          <rdf:li xml:lang="x-default">${data.project || ''}</rdf:li>
        </rdf:Alt>
      </dc:description>
      
      <xmp:CreatorTool>Q&amp;B</xmp:CreatorTool>
      <xmp:CreateDate>${now}</xmp:CreateDate>
      <xmp:ModifyDate>${now}</xmp:ModifyDate>
      <pdfaid:part>3</pdfaid:part>
      <pdfaid:conformance>A</pdfaid:conformance>
    </rdf:Description>
  </rdf:RDF>
</x:xmpmeta>
<?xpacket end="w"?>`;
    }

    async addMetadataToPDF(pdfBuffer, data, documentType, fileName) {
        // Charger le PDF
        const pdfDoc = await PDFDocument.load(pdfBuffer);
        
        // Définir les métadonnées standard du document
        const docTypeName = documentType === 'INVOICE' ? 'Facture' : 'Devis';
        // pdfDoc.setTitle(`${docTypeName} ${data.docNumber}`);
        // pdfDoc.setAuthor('ACME');
        // pdfDoc.setSubject(data.project || '');
        // pdfDoc.setProducer('ACME Q&B Generator');
        // pdfDoc.setCreator('PDFDocumentService');
        
        // Encoder nos tags personnalisés dans les mots-clés
        // Format : "tag:valeur, tag:valeur" - permet de garder une structure lisible et extractible
        const customTags = [
            `DocumentType:${documentType}`,
            `DocumentFileName:${fileName}`,
            `Version:1.0`,
            `ConformanceLevel:${documentType === 'INVOICE' ? 'FacturX-1.0.7' : 'Standard'}`
        ];
        
        // Ajouter des tags business additionnels
        customTags.push(`ClientName:${data.clientName}`);
        customTags.push(`DocNumber:${data.docNumber}`);
        customTags.push(`DocDate:${data.date}`);
        customTags.push(`DocAmount:${data.total.toFixed(2)}`);
        
        // Construire la chaîne de mots-clés
        const keywordsString = customTags.join(', ');
        pdfDoc.setKeywords([keywordsString]);
        
        // Construire une description détaillée
        let description = `${docTypeName} n° ${data.docNumber} pour ${data.clientName}`;
        description += `\nProjet : ${data.project}`;
        description += `\nDate : ${data.date}`;
        description += `\nMontant : ${data.total.toFixed(2)}€ HT`;
        
        // Ajouter une indication si c'est une facture avec Factur-X
        if (documentType === 'INVOICE') {
            description += "\nCe document contient des données Factur-X en pièce jointe.";
        }
        console.log(customTags);
        pdfDoc.setSubject(description);
        
        // Sauvegarder le document
        return await pdfDoc.save();
    }

    // Méthodes existantes pour les devis
    async generateQuoteContent(data) {
        const firstPage = [...await this.generateQuoteFirstPage(data)];
        return [
            ...firstPage,
            { text: '', pageBreak: 'after' },
            ...this.generateGeneralConditionsPage()
        ];
    }

    // Méthode pour générer le pied de page
    generateFooter(isBill = false) {
        // Footer de base avec le texte
        const footerElements = [
            {
                text: 'ACME - SAS à associé unique - Inscrite au RCS de New York sous le numéro 123 456 789 - SIRET : 123 456 789 00012 - APE : 7022Z',
                alignment: 'center',
                style: 'footer'
            },
            {
                text: 'Siège social : 123 main street, New York, NY 10001 - Téléphone : 01 02 03 04 05',
                alignment: 'center',
                style: 'footer'
            }
        ];
        
        // Si c'est une facture, ajouter le logo Factur-X avec positionnement absolu
        if (isBill) {
            try {
                const logoFXmInstance = new LogoFXm();
                const logoContent = logoFXmInstance.generateLogo();
                
                // Ajouter le logo avec un positionnement qui assure sa visibilité
                footerElements.push({
                    image: logoContent.image || logoContent,
                    width: 30,
                    height: 30,
                    alignment: 'right',
                    margin: [0, -5, 8, 0]
                });
            } catch (error) {
                console.error('Erreur lors de la génération du logo Factur-X:', error);
            }
        }
        
        return footerElements;
    }
}

export default PDFDocumentService;